<template>
  <v-dialog
    :value="value"
    @input="$emit('input', false)"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    class="settings-dialog-wrapper">
    <v-card dark tile class="settings-glass-card d-flex flex-column">
      <div class="cinematic-header" :class="{ 'support-header': group && group.isSupport }">
        <div
          v-if="!(group && group.isSupport)"
          class="header-bg-image"
          :style="getDisplayAvatar(group) ? `background-image: url('${getDisplayAvatar(group)}')` : ''"></div>

        <div class="header-overlay" :class="{ 'support-overlay': group && group.isSupport }"></div>

        <div class="header-top-bar">
          <v-btn icon large class="close-btn glass-btn" @click="$emit('input', false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="header-content d-flex flex-column justify-center align-center">
          <div class="avatar-wrapper">
            <!-- SUPPORT: Spezielles Icon statt Bild -->
            <template v-if="group && group.isSupport">
              <div class="support-icon-wrapper">
                <v-icon size="64" color="white">mdi-face-agent</v-icon>
              </div>
            </template>
            <template v-else-if="group && group.isDirect">
              <v-avatar size="140" color="primary darken-3" class="elevation-10">
                <v-img v-if="getDisplayAvatar(group)" :src="getDisplayAvatar(group)"></v-img>
                <span v-else class="white--text display-3 font-weight-bold">{{ getGroupInitials(getDisplayName(group)) }}</span>
              </v-avatar>
            </template>
            <ChatGroupPicture
              v-else-if="group"
              :isAdmin="isGroupAdmin(group)"
              :group-id="group._id"
              :initial-picture="group.bild ? '/img/profilbilder/' + group.bild : null"
              :width="140"
              :height="140"
              @updatedPicture="handlePictureUpdate" />
          </div>

          <h2 class="text-h5 mb-0 font-weight-bold mt-4 white--text text-shadow text-center px-4">
            {{ getDisplayName(group) }}
          </h2>
          <span
            v-if="group && group.isSupport"
            class="subtitle-2 blue--text text--lighten-2 opacity-80 mt-2"
            style="max-width: 280px; text-align: center; line-height: 1.4">
            Wir helfen dir gerne bei Fragen rund um die App!
          </span>
          <span v-else-if="!group?.isDirect" class="subtitle-2 grey--text text--lighten-3 opacity-80 mt-1">
            {{ group && group.members ? group.members.length + " Mitglieder" : "" }}
          </span>
        </div>
      </div>

      <v-overlay :value="isSaving" absolute color="#000" opacity="0.7" style="z-index: 10">
        <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
      </v-overlay>

      <div class="content-scroll-area flex-grow-1">
        <v-container class="pa-0">
          <!-- Support: Kein Name/Beschreibung bearbeitbar -->
          <div v-if="group && group.isSupport" class="px-6 pt-6">
            <div class="support-info-card pa-4 rounded-lg mb-2">
              <div class="d-flex align-center mb-3">
                <v-icon color="blue lighten-2" class="mr-3">mdi-information-outline</v-icon>
                <span class="font-weight-bold body-1 white--text">Über den App Support</span>
              </div>
              <p class="body-2 grey--text text--lighten-1 mb-0" style="line-height: 1.6">
                Hier kannst du uns direkt bei Fragen, Problemen oder Feedback erreichen. Wir antworten so schnell wie möglich. 💪
              </p>
            </div>
          </div>

          <div class="px-6 pt-6" v-else-if="!group?.isDirect">
            <v-form ref="form" v-model="isValid">
              <v-text-field
                v-model="editableName"
                :disabled="!isGroupAdmin(group)"
                label="Gruppenname"
                hide-details
                class="glass-input mb-4"
                dark
                filled
                rounded
                dense></v-text-field>

              <v-textarea
                :disabled="!isGroupAdmin(group)"
                v-model="editableDescription"
                label="Beschreibung"
                hide-details
                class="glass-input"
                dark
                filled
                rounded
                dense
                rows="2"
                auto-grow></v-textarea>
            </v-form>
          </div>

          <v-tabs v-model="tab" dark background-color="transparent" grow class="glass-tabs mt-6 mb-0" slider-color="primary">
            <v-tab class="pb-2" v-if="!group?.isDirect"><v-icon left small>mdi-account-group</v-icon> Mitglieder</v-tab>
            <v-tab class="pb-2"><v-icon left small>mdi-view-grid</v-icon> Medien</v-tab>
          </v-tabs>

          <v-divider style="border-color: rgba(255, 255, 255, 0.08)"></v-divider>

          <v-tabs-items dark v-model="tab" style="background-color: transparent">
            <v-tab-item v-if="!group?.isDirect">
              <div class="px-4 pt-4" v-if="isGroupAdmin(group)">
                <user-search
                  v-model="userToAdd"
                  label="Mitglied hinzufügen"
                  instructor
                  :returnUser="true"
                  :ignoreRights="true"
                  @update="handleAddUser"
                  class="glass-input mb-2" />
              </div>

              <v-list dense color="transparent" class="pa-0 mt-2">
                <v-list-item
                  @click="viewUserProfile(member.userId)"
                  v-for="member in group.members"
                  :key="member.userId._id"
                  class="member-list-item px-4 py-2">
                  <v-list-item-avatar size="44" class="mr-4 my-0">
                    <v-img v-if="member.userId.bild" :src="'/img/profilbilder/' + member.userId.bild"></v-img>
                    <span v-else class="white--text headline">{{ getInitials(member.userId) }}</span>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold body-1" :class="{ 'orange--text': !hasChatAccess(member) }">
                      {{ member.userId.vorname }} {{ member.userId.nachname }}
                    </v-list-item-title>

                    <v-list-item-subtitle
                      class="caption mt-1"
                      :class="{
                        'orange--text': !hasChatAccess(member),
                        'primary--text': hasChatAccess(member) && getPresence(member).isOnline,
                        'grey--text': hasChatAccess(member) && !getPresence(member).isOnline,
                      }">
                      {{ getMemberStatusText(member) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action class="d-flex flex-row align-center my-0">
                    <v-tooltip v-if="!hasChatAccess(member)" bottom content-class="glass-menu">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon color="warning" small class="mr-2" v-bind="attrs" v-on="on" @click.stop> mdi-alert-circle-outline </v-icon>
                      </template>
                      <span class="caption">Keine Chat-Rechte</span>
                    </v-tooltip>

                    <v-menu v-if="isGroupAdmin(group)" offset-y left content-class="glass-menu">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon v-bind="attrs" v-on.stop="on" color="grey lighten-1">
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item @click="toggleAdmin(member)">
                          <v-list-item-icon><v-icon>mdi-shield-check</v-icon></v-list-item-icon>
                          <v-list-item-title>{{ member.role === "admin" ? "Admin entfernen" : "Zum Admin machen" }}</v-list-item-title>
                        </v-list-item>
                        <v-divider></v-divider>
                        <v-list-item @click="kickMember(member)">
                          <v-list-item-icon><v-icon color="red">mdi-account-remove</v-icon></v-list-item-icon>
                          <v-list-item-title class="red--text">Aus Gruppe entfernen</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-list-item-action>
                </v-list-item>
              </v-list>

              <div class="px-4 py-2" v-if="!group.isSupport && !group.isDirect">
                <v-btn block text color="red lighten-1" class="glass-btn-danger" @click="showLeaveWarning">
                  <v-icon left>mdi-exit-run</v-icon> Gruppe verlassen
                </v-btn>
              </div>
            </v-tab-item>

            <v-tab-item>
              <div class="media-grid pa-1">
                <div v-if="isMediaLoading && media.length === 0" class="text-center py-8">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                </div>

                <div v-if="!isMediaLoading && media.length === 0" class="empty-media-state">
                  <v-icon size="48" color="rgba(255,255,255,0.2)">mdi-image-multiple-outline</v-icon>
                  <p>Keine Medien</p>
                </div>

                <div v-for="item in media" :key="item._id" class="media-grid-item" @click="openMediaViewer(item)">
                  <div class="media-aspect-ratio">
                    <SecuredMedia noObserver :filename="item.fileUrl" :mediaType="item.messageType" class="media-content-fill" />
                    <div v-if="item.messageType === 'video'" class="video-overlay">
                      <v-icon color="white">mdi-play-circle-outline</v-icon>
                    </div>
                  </div>
                </div>
              </div>

              <v-intersect v-if="mediaHasMore && !isMediaLoading" @enter="loadMoreMedia" style="height: 20px"></v-intersect>

              <div v-if="isMediaLoading && media.length > 0" class="text-center py-4">
                <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
              </div>
            </v-tab-item>
          </v-tabs-items>
          <div class="px-4 py-2" v-if="group.isDirect">
            <v-btn block text color="red lighten-1" class="glass-btn-danger" @click="showDeleteWarning">
              <v-icon left>mdi-delete-outline</v-icon> Chat löschen
            </v-btn>
          </div>
        </v-container>

        <div style="height: 80px"></div>
      </div>

      <div class="settings-footer px-4 py-3 d-flex align-center">
        <v-btn text color="grey lighten-1" @click="$emit('input', false)" :disabled="isSaving"> Schließen </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="!group?.isDirect && !group?.isSupport"
          color="primary"
          rounded
          depressed
          large
          class="px-8 font-weight-bold"
          @click="saveChanges"
          :loading="isSaving"
          :disabled="!isValid">
          Speichern
        </v-btn>
      </div>
    </v-card>

    <v-dialog v-model="kickConfirmDialog" max-width="350" persistent>
      <v-card dark class="glass-menu pa-4">
        <div class="text-h6 font-weight-bold mb-2">Entfernen?</div>
        <div class="grey--text text--lighten-1 body-2 mb-4" v-if="memberToKick">
          Möchtest du <strong>{{ memberToKick.userId.vorname }}</strong> wirklich entfernen?
        </div>
        <div class="d-flex justify-end">
          <v-btn text @click="kickConfirmDialog = false">Abbrechen</v-btn>
          <v-btn color="red" text @click="confirmKick" :loading="isBusyKicking">Entfernen</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="leaveConfirmDialog" max-width="350" persistent>
      <v-card dark class="glass-menu pa-4">
        <div class="text-h6 font-weight-bold mb-2">Gruppe verlassen?</div>
        <div class="grey--text text--lighten-1 body-2 mb-4">Du kannst nicht selbstständig wieder beitreten.</div>
        <div class="d-flex justify-end">
          <v-btn text @click="leaveConfirmDialog = false">Bleiben</v-btn>
          <v-btn color="red" text @click="confirmLeave" :loading="isLeaving">Verlassen</v-btn>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteConfirmDialog" max-width="350" persistent>
      <v-card dark class="glass-menu pa-4">
        <div class="text-h6 font-weight-bold mb-2">Chat löschen?</div>
        <div class="grey--text text--lighten-1 body-2 mb-4">
          Der Chat wird für dich ausgeblendet. Der Chatpartner kann die Nachrichten weiterhin sehen.
        </div>
        <div class="d-flex justify-end">
          <v-btn text @click="deleteConfirmDialog = false">Behalten</v-btn>
          <v-btn color="red" text @click="confirmDelete" :loading="isDeleting">Löschen</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <MediaViewerDialog v-if="selectedMediaItem" v-model="showMediaViewer" :item="selectedMediaItem" :key="selectedMediaItem._id" />
  </v-dialog>
</template>

<script>
import api from "@/api"
import { mapState, mapActions, mapGetters } from "vuex"
import ChatGroupPicture from "./chatGroupPicture.vue"
import userSearch from "@/components/userSearch"
import SecuredMedia from "./securedMedia.vue"
import MediaViewerDialog from "./mediaViewerDialog.vue"

export default {
  name: "ChatGroupSettingsDialog",
  components: { ChatGroupPicture, userSearch, SecuredMedia, MediaViewerDialog },

  props: {
    value: Boolean,
    group: Object,
  },

  data() {
    return {
      isSaving: false,
      isValid: false,
      editableName: "",
      editableDescription: "",
      editableBild: "",
      kickConfirmDialog: false,
      memberToKick: null,
      isBusyKicking: false,
      userToAdd: null,
      leaveConfirmDialog: false,
      deleteConfirmDialog: false,
      isLeaving: false,
      isDeleting: false,
      tab: 0,
      media: [],
      mediaPage: 0,
      mediaHasMore: true,
      isMediaLoading: false,
      showMediaViewer: false,
      selectedMediaItem: null,
    }
  },

  watch: {
    tab(newTab) {
      if (newTab === 1 && this.media.length === 0) {
        this.loadMoreMedia()
      }
    },
    value: {
      handler(isOpening) {
        if (isOpening && this.group) {
          this.editableName = this.group.name
          this.editableDescription = this.group.description || ""
          this.editableBild = this.group.bild || null
        }
      },
      immediate: true,
    },
  },

  computed: {
    ...mapState("userStore", ["user"]),
    ...mapGetters("chatStore", ["getPresenceByUserId"]),
  },

  methods: {
    ...mapActions("chatStore", {
      saveGroupDetails: "saveGroupDetails",
      kickGroupMember: "kickGroupMember",
      updateGroupMemberRole: "updateGroupMemberRole",
      addMembersToGroup: "addMembersToGroup",
      viewUserProfile: "viewUserProfile",
      leaveGroup: "leaveGroup",
      deleteGroup: "deleteGroup",
    }),

    getOtherMember(group) {
      if (!group || !group.members || !this.user) return null
      return group.members.find((m) => m.userId && (m.userId._id || m.userId) !== this.user._id)
    },
    getDisplayAvatar(group) {
      if (group?.isDirect) {
        const other = this.getOtherMember(group)
        return other && other.userId && other.userId.bild ? "/img/profilbilder/" + other.userId.bild : null
      }
      return group?.bild ? "/img/profilbilder/" + group.bild : null
    },
    getDisplayName(group) {
      if (group?.isSupport) return "App Support Chat"
      if (group?.isDirect) {
        const other = this.getOtherMember(group)
        return other && other.userId ? `${other.userId.vorname} ${other.userId.nachname}` : "Gelöschter Nutzer"
      }
      return group ? group.name : "Gruppe"
    },
    getGroupInitials(name) {
      if (!name) return "G"
      const parts = name.trim().split(" ")
      if (parts.length > 1) return (parts[0][0] + parts[1][0]).toUpperCase()
      return name.substring(0, 2).toUpperCase()
    },

    openMediaViewer(item) {
      this.selectedMediaItem = item
      this.showMediaViewer = true
    },

    async loadMoreMedia() {
      if (!this.mediaHasMore || this.isMediaLoading) return
      this.isMediaLoading = true
      try {
        const { data: newMedia } = await api.fetchGroupMedia(this.group._id, this.mediaPage)
        if (newMedia.length < 21) {
          this.mediaHasMore = false
        }
        this.media.push(...newMedia)
        this.mediaPage++
      } catch (err) {
        console.error("Error loading media", err)
      } finally {
        this.isMediaLoading = false
      }
    },

    showLeaveWarning() {
      this.kickConfirmDialog = false
      this.leaveConfirmDialog = true
    },
    showDeleteWarning() {
      this.kickConfirmDialog = false
      this.leaveConfirmDialog = false
      this.deleteConfirmDialog = true
    },

    async confirmLeave() {
      this.isLeaving = true
      try {
        await this.leaveGroup(this.group._id)
        this.$emit("input", false)
        this.leaveConfirmDialog = false
      } catch (err) {
        this.$toast.error(err.response?.data?.msg || "Fehler beim Verlassen")
      } finally {
        this.isLeaving = false
      }
    },
    async confirmDelete() {
      this.isDeleting = true
      try {
        // Ruft die eben erstellte Store-Action auf
        await this.deleteGroup(this.group._id)
        this.$emit("input", false) // Schließt den Einstellungs-Dialog
        this.deleteConfirmDialog = false
      } catch (err) {
        this.$toast.error(err.message || "Fehler beim Löschen")
      } finally {
        this.isDeleting = false
      }
    },

    async handleAddUser(userObject) {
      if (!userObject) return
      const isAlreadyMember = this.group.members.some((m) => m.userId._id === userObject._id)
      if (isAlreadyMember) {
        this.$nextTick(() => {
          this.userToAdd = null
        })
        return
      }
      this.isBusyKicking = true
      try {
        await this.addMembersToGroup({ groupId: this.group._id, memberIds: [userObject._id] })
      } catch (err) {
        console.error("Error adding member", err)
      } finally {
        this.isBusyKicking = false
        this.$nextTick(() => {
          this.userToAdd = null
        })
      }
    },

    getInitials(user) {
      if (!user) return "?"
      const v = user.vorname || ""
      const n = user.nachname || ""
      return `${v.charAt(0).toUpperCase()}${n.charAt(0).toUpperCase()}`
    },

    getPresence(member) {
      if (!member || !member.userId) return { isOnline: false }
      return this.getPresenceByUserId(member.userId._id) || { isOnline: false }
    },

    getMemberStatusText(member) {
      if (this.user && member.userId._id === this.user._id) {
        const myRole = member.role === "admin" ? "Admin" : "Mitglied"
        return `Ich - ${myRole}`
      }
      let roleString = member.role === "admin" ? " - Admin" : " - Mitglied"
      const presence = this.getPresence(member)
      if (presence.isOnline) return `Online${roleString}`
      if (presence.lastSeen) {
        const date = new Date(presence.lastSeen)
        return `Zuletzt online: ${date.toLocaleDateString("de-CH")} ${date.toLocaleTimeString("de-CH")}`
      }
      return `Offline${roleString}`
    },

    isGroupAdmin(group) {
      if (!this.user || !group) return false
      return group.members.some((m) => m.userId._id === this.user._id && m.role === "admin")
    },

    hasChatAccess(member) {
      if (!member || !member.userId) return false
      const user = member.userId
      return (user.specialRights && user.specialRights.length > 0) || (user.userRoles && user.userRoles.length > 0)
    },

    handlePictureUpdate(newBildUrl) {
      this.editableBild = newBildUrl
      this.saveChanges(true)
    },

    async saveChanges(onlyImage = false) {
      if (!onlyImage && !this.$refs.form.validate()) return
      this.isSaving = true
      try {
        const payload = { name: this.editableName, description: this.editableDescription, bild: this.editableBild }
        await this.saveGroupDetails({ groupId: this.group._id, updateData: payload })
        this.$emit("group-updated", payload)
        if (!onlyImage) this.$emit("input", false)
      } catch (err) {
        console.error("Save failed", err)
      } finally {
        this.isSaving = false
      }
    },

    kickMember(member) {
      if (member.userId._id === this.user._id) return
      this.memberToKick = member
      this.kickConfirmDialog = true
    },

    async confirmKick() {
      if (!this.memberToKick) return
      this.isBusyKicking = true
      try {
        await this.kickGroupMember({ groupId: this.group._id, userIdToRemove: this.memberToKick.userId._id })
      } catch (err) {
      } finally {
        this.isBusyKicking = false
        this.kickConfirmDialog = false
        this.memberToKick = null
      }
    },

    async toggleAdmin(member) {
      if (member.userId._id === this.user._id) return
      this.isBusyKicking = true
      const newRole = member.role === "admin" ? "member" : "admin"
      try {
        await this.updateGroupMemberRole({ groupId: this.group._id, userIdToUpdate: member.userId._id, newRole: newRole })
      } catch (err) {
      } finally {
        this.isBusyKicking = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
/* * 1. BASIS LAYOUT
 */
.settings-glass-card {
  background-color: #121212 !important;
  height: 100vh; /* Volle Höhe */
  overflow: hidden;
}

.content-scroll-area {
  overflow-y: auto;
  /* Smooth Scroll */
  -webkit-overflow-scrolling: touch;
}

/* * 2. CINEMATIC HEADER
 */
.cinematic-header {
  position: relative;
  /* SAFE AREA FIX: Padding oben für Notch/Statusbar */
  padding-top: env(safe-area-inset-top);

  height: 320px; /* Großzügige Höhe */
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #1e1e1e;
  flex-shrink: 0;
  overflow: hidden;
}

/* Hintergrundbild */
.header-bg-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center center;
  /* Starker Blur + Dunkler */
  filter: blur(40px) saturate(140%) brightness(0.5);
  transform: scale(1.2); /* Ränder verstecken */
  z-index: 0;
}

/* Gradient Overlay nach unten */
.header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* Verlauf von transparent zu #121212 (Background der Karte) */
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.1) 0%, rgba(18, 18, 18, 0.6) 70%, #121212 100%);
  z-index: 1;
}

.header-top-bar {
  position: absolute;
  top: env(safe-area-inset-top);
  left: 0;
  right: 0;
  padding: 8px 16px;
  z-index: 10;
  display: flex;
  justify-content: flex-end;
}

.glass-btn {
  background: rgba(0, 0, 0, 0.3) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.header-content {
  position: relative;
  z-index: 2;
  width: 100%;
  margin-top: 20px;
}

.avatar-wrapper {
  /* Schatten für den Avatar */
  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.5));
}

.text-shadow {
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.8);
}
.opacity-80 {
  opacity: 0.8;
}

/* * 3. INPUTS & UI ELEMENTS
 */
::v-deep .glass-input .v-input__slot {
  background: rgba(255, 255, 255, 0.04) !important;
  border: 1px solid rgba(255, 255, 255, 0.04);
  transition: all 0.3s;
}
::v-deep .glass-input.v-input--is-focused .v-input__slot {
  background: rgba(255, 255, 255, 0.08) !important;
  border-color: rgba(255, 255, 255, 0.2);
}

.glass-tabs {
  /* Keine Border unten, wirkt moderner */
}
::v-deep .v-tab {
  text-transform: none !important;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.5) !important;
}
::v-deep .v-tab--active {
  color: white !important;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
}

/* * 4. CLEAN LIST
 */
.member-list-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.02);
}

.glass-btn-danger {
  background: rgba(255, 82, 82, 0.08) !important;
  border: 1px solid rgba(255, 82, 82, 0.2);
}

/* * 5. MEDIA GRID (INSTAGRAM STYLE) 
 */
.media-grid {
  display: grid;
  /* Automatische Spalten, mind. 100px breit */
  grid-template-columns: repeat(3, 1fr);
  gap: 2px; /* Sehr feiner Abstand */
}

.media-grid-item {
  position: relative;
  cursor: pointer;
  overflow: hidden;
  background-color: #000;
}

/* Der Aspect Ratio Hack: Quadratisch */
.media-aspect-ratio {
  width: 100%;
  padding-bottom: 100%; /* Macht es quadratisch (1:1) */
  position: relative;
}

/* Das Bild zwingen, den Platz zu füllen */
.media-content-fill {
  position: absolute !important;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100% !important;
  height: 100% !important;
}

/* WICHTIG: Override für dein SecuredMedia Limit */
::v-deep .secured-media-wrapper {
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
}
::v-deep .secured-media-content {
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  max-height: none !important;
  object-fit: contain !important; /* Füllt das Quadrat */
  border-radius: 0 !important; /* Eckig im Grid */
  margin: 0 !important;
}

/* Video Overlay Icon */
.video-overlay {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 2;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
}

.empty-media-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: rgba(255, 255, 255, 0.3);
}

/* * 6. FOOTER BAR
 */
.settings-footer {
  background: rgba(18, 18, 18, 0.9);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
  z-index: 10;
  /* Safe Area unten */
  padding-bottom: calc(12px + env(safe-area-inset-bottom)) !important;
}

/* Dialog Glass Menus */
::v-deep .glass-menu {
  background-color: rgba(30, 30, 30, 0.85) !important;
  backdrop-filter: blur(30px) saturate(180%) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 16px !important;
}

/* 7. SUPPORT HEADER SPECIAL */
.support-header {
  background: linear-gradient(135deg, #0d47a1 0%, #1565c0 40%, #1976d2 100%) !important;
}
.support-overlay {
  background: linear-gradient(to bottom, rgba(13, 71, 161, 0.2) 0%, rgba(18, 18, 18, 0.7) 70%, #121212 100%) !important;
}
.support-icon-wrapper {
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05));
  border: 2px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3), 0 0 40px rgba(33, 150, 243, 0.15);
}
.support-info-card {
  background: rgba(33, 150, 243, 0.08) !important;
  border: 1px solid rgba(33, 150, 243, 0.2);
}
</style>
